import { useId, type ReactNode } from "react";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { buttonClass } from "@/components/buttonStyles";
import {
  LockedButton,
  Pill,
} from "@/components/bond-submission/SubmissionShell";
import type { AccountProgramCapacity } from "@/lib/brokerAccounts";
import type { BondSubmission } from "@/lib/brokerBonds";
import type { CapacityProject } from "@/lib/capacityRequest";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { submissionActionRow } from "@/lib/submissionShell";
import {
  NO_PROGRAM_SENTENCE,
  currentBond,
  programLimits,
  type CurrentBondView,
  type ProgramLimitsView,
} from "@/lib/submissionProfile";

// BRK-S415, BRK-S502 — the Submission Profile's current-bond card, the
// October wireframe's box (a): its eyebrow (CURRENT BOND / CURRENT BONDS /
// FINAL STATUS) is the box's heading, Edit Bond Request sits at its right
// beside the collapse; then the bond(s) and the status pill, the Form line,
// PMS's grey note strip (`currentBondNote`, served display copy) and the
// PROGRAM LIMITS block (the single and aggregate meters and PMS's verdict).
// It renders `src/lib/submissionProfile.ts`'s reads and decides nothing (#7).
//
// Mobile-first: one column; the pill wraps under the title and the meter
// captions wrap rather than widening the page at 375.

type Order = Pick<
  BondSubmission,
  "id" | "program" | "capacityCheck" | "actions"
> & {
  bonds?: unknown;
  lifecycle?: unknown;
  currentBondNote?: string | null;
};

export function CurrentBondDetails({
  order,
  programName,
  capacity,
  projects,
  onEdit,
}: {
  order: Order;
  /** The account's program, named ("Signal Express"); null when unread. */
  programName: string | null;
  /** The account's utilization; null when unread or not published. */
  capacity: AccountProgramCapacity | null;
  /** The account's capacity-projects read; null when unread or failed. */
  projects: readonly CapacityProject[] | null;
  /** Edit Bond Request, when PMS grants it. */
  onEdit: () => void;
}) {
  const bond = currentBond(order);
  const limits = programLimits({
    order,
    capacity,
    projects,
    advanceOffered: programName === "Signal Express",
  });
  if (bond === null && limits === null) return null;
  // The fixed row's own read of `actions.edit`, so the card and the row can
  // never disagree.
  const edit = submissionActionRow(order).find((a) => a.key === "edit");
  const button = buttonClass({ variant: "secondary", size: "sm" });
  const actions =
    edit === undefined ? undefined : edit.enabled ? (
      <button type="button" onClick={onEdit} className={button}>
        {edit.label}
      </button>
    ) : (
      <LockedButton
        label={edit.label}
        reason={edit.reason}
        className={button}
      />
    );
  return (
    <CollapsibleBox title={bond?.eyebrow ?? "Current bond"} actions={actions}>
      <div className="flex min-w-0 flex-col gap-6">
        {bond && <CurrentBondCard bond={bond} />}
        {limits && (
          <ProgramLimitsBlock view={limits} programName={programName} />
        )}
      </div>
    </CollapsibleBox>
  );
}

function CurrentBondCard({ bond }: { bond: CurrentBondView }) {
  const headingId = useId();
  return (
    <section
      // "Current bond Bid bond": the eyebrow says which bond this is.
      aria-label={`${bond.eyebrow} ${bond.title}`}
      className="flex min-w-0 flex-col gap-2"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h3 id={headingId} className="break-words text-heading text-brk-text">
          {bond.title}
        </h3>
        <Pill pill={bond.pill} />
      </div>
      {bond.form !== null && (
        <p className="break-words text-sm text-brk-dim">
          {bond.form.startsWith("Last bond: ") ? "" : "Form: "}
          <span className="text-brk-text">{bond.form}</span>
        </p>
      )}
      {bond.note !== null && (
        <p
          data-note-strip
          className="break-words rounded-lg bg-brk-bg px-3 py-2 text-sm text-brk-dim"
        >
          {bond.note}
        </p>
      )}
    </section>
  );
}

/** Percent for a bar's width and `aria-valuenow`: clamped to the bar. */
const clamp = (n: number) => Math.min(100, Math.max(0, n));
const pctOf = (n: number, of: number) => clamp((n / of) * 100);
const money = formatPreapprovalMoney;

function ProgramLimitsBlock({
  view,
  programName,
}: {
  view: ProgramLimitsView;
  programName: string | null;
}) {
  const headingId = useId();
  const title =
    view.noProgram || programName === null
      ? "Program limits"
      : `Program limits · ${programName}`;
  return (
    <section
      aria-labelledby={headingId}
      className="flex min-w-0 flex-col gap-4 border-t border-brk-border pt-4"
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <h3
          id={headingId}
          className="break-words text-sm font-semibold text-brk-text"
        >
          {title}
        </h3>
        {view.pill && <Pill pill={view.pill} />}
      </div>
      {view.noProgram && (
        <p className="text-sm text-brk-danger">{NO_PROGRAM_SENTENCE}</p>
      )}
      {view.single && (
        <Meter
          label="Single bond limit"
          used={`${String(view.single.percent)}% used`}
          over={view.overLimit === "single"}
          caption={[
            `${money(view.single.bond)} this bond`,
            `of ${money(view.single.limit)} per bond`,
          ]}
          segments={[
            { pct: pctOf(view.single.bond, view.single.limit), strong: true },
          ]}
        />
      )}
      {view.aggregate && (
        <AggregateMeter
          aggregate={view.aggregate}
          over={view.overLimit === "aggregate"}
        />
      )}
      {view.warning !== null && (
        <p
          role="note"
          className="rounded-lg border border-brk-danger/40 bg-brk-danger/10 p-3 text-sm text-brk-danger"
        >
          {view.warning}
        </p>
      )}
    </section>
  );
}

function AggregateMeter({
  aggregate,
  over,
}: {
  aggregate: NonNullable<ProgramLimitsView["aggregate"]>;
  over: boolean;
}) {
  const { other, thisBond, limit, availableAfter, onceIssued } = aggregate;
  const counted = other + (thisBond ?? 0);
  const available =
    availableAfter < 0 ? (
      <span className="text-brk-danger">
        {`${money(-availableAfter)} over the aggregate of ${money(limit)}`}
      </span>
    ) : thisBond === null ? (
      `${money(availableAfter)} available of ${money(limit)}`
    ) : (
      `${money(availableAfter)} available after this bond of ${money(limit)}`
    );
  return (
    <Meter
      label="Aggregate limit"
      used={`${String(Math.round((counted / limit) * 100))}% used`}
      over={over}
      caption={[
        <Legend key="other" strong={false}>
          Other bonds {money(other)}
        </Legend>,
        thisBond === null ? (
          <span key="this">This bond · released (not counted)</span>
        ) : (
          <Legend key="this" strong>
            This bond {money(thisBond)}
            {onceIssued ? " (once issued)" : ""}
          </Legend>
        ),
        <span key="available">{available}</span>,
      ]}
      segments={[
        { pct: pctOf(other, limit), strong: false },
        {
          pct: thisBond === null ? 0 : clamp(pctOf(thisBond, limit)),
          strong: true,
        },
      ]}
      valueNow={pctOf(counted, limit)}
      valueText={`Other bonds ${money(other)} · ${
        thisBond === null
          ? "this bond released"
          : `this bond ${money(thisBond)}`
      } · of ${money(limit)}`}
    />
  );
}

/** A legend entry: the swatch of its segment, then the words. */
function Legend({
  strong,
  children,
}: {
  strong: boolean;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={`inline-block h-2.5 w-2.5 rounded-sm ${
          strong ? "bg-brk-accent" : "bg-brk-accent/40"
        }`}
      />
      {children}
    </span>
  );
}

/**
 * One meter: its name and how much is used, a bar of one or two segments
 * (other bonds lighter, this bond solid; red when PMS says this limit is
 * over), and the figures under it, each its own phrase so they wrap whole at
 * 375.
 */
function Meter({
  label,
  used,
  over,
  caption,
  segments,
  valueNow,
  valueText,
}: {
  label: string;
  used: string;
  over: boolean;
  caption: ReactNode[];
  segments: { pct: number; strong: boolean }[];
  valueNow?: number;
  valueText?: string;
}) {
  const now = Math.round(
    valueNow ?? segments.reduce((sum, s) => sum + s.pct, 0),
  );
  const text =
    valueText ??
    caption.filter((c): c is string => typeof c === "string").join(" · ");
  return (
    <div
      className="flex min-w-0 flex-col gap-1.5"
      data-over={over || undefined}
    >
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-medium text-brk-text">{label}</span>
        <span
          className={`text-xs ${over ? "font-medium text-brk-danger" : "text-brk-dim"}`}
        >
          {used}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamp(now)}
        aria-valuetext={text}
        className="flex h-2 w-full overflow-hidden rounded-full bg-brk-border"
      >
        {segments.map((s, i) => (
          <div
            key={i}
            className={`h-full ${
              over
                ? s.strong
                  ? "bg-brk-danger"
                  : "bg-brk-danger/40"
                : s.strong
                  ? "bg-brk-accent"
                  : "bg-brk-accent/40"
            }`}
            style={{ width: `${String(s.pct)}%` }}
          />
        ))}
      </div>
      <p className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-brk-dim">
        {caption.map((c, i) => (
          <span key={i} className="break-words">
            {c}
          </span>
        ))}
      </p>
    </div>
  );
}
